export const jmdsProposal = {

  meta: {
    title: "Multi-Domain Website Platform Proposal",
    clientName: "Will's Plumbing",
    siteDomain: "",
    url: "",
    date: "2026-07-23",
    quoteNumber: "Q-2026-WP-PLATFORM",
    preparedBy: "Jason Toomer",
    summary: [
  "Hi Will, thank you for the opportunity to prepare this proposal for the development of your multi-domain website platform.",
  "I appreciate this represents a significant investment. However, you are not purchasing another website, you are investing in a bespoke software platform that will become the foundation of your Exact Match Domain (EMD) strategy for many years to come.",
  "Viewed another way, based on your current portfolio of approximately 350 domains, the platform investment equates to around A$84 per potential website. As additional domains and websites are added over time, the effective cost per website continues to reduce while the same platform continues to support, manage and publish them.",
  "Rather than building and maintaining hundreds of independent websites, this solution creates a single, centrally managed platform capable of supporting your existing portfolio while remaining scalable as your business grows.",
  "In practical terms, you will receive one hosted Next.js application, one central Supabase database and one secure management dashboard. Each website will exist as its own independent record with its own content, branding, SEO configuration, domain settings and Google Analytics connection.",
  "When a visitor accesses one of your connected domains, the platform automatically identifies the domain and serves the correct website. Every website remains independently editable and publishable while sharing the same proven software platform underneath.",
  "The platform effectively becomes a 'website factory', allowing authorised users to create, configure and publish additional websites through the management dashboard without rebuilding software or duplicating code. Where complete content, branding and configuration are available, websites can be rendered live and published through the platform as part of the implementation.",
  "Website content, SEO settings and configuration data will be stored securely within Supabase rather than hundreds of JavaScript files. This provides a future-proof content management solution that allows websites to evolve without modifying the application itself.",
  "The platform is also designed for long-term expansion. New templates, automation, reporting, CRM integrations and AI-assisted content creation can all be added over time without redesigning the underlying system, protecting your investment as your EMD strategy continues to grow."
],
  },

  hero: {
    eyebrow: "Multi-domain website management platform",
    heading: "One platform to create, manage and publish hundreds of websites",
    intro:
      "A bespoke Next.js and Supabase platform that connects your domain portfolio to one centrally managed application, allowing each domain to display independent content, branding, SEO, analytics and lead-generation information.",
    contact: {
      agencyName: "2merative Web Design",
      contactName: "Jason Toomer",
      email: "hello@2merative.com",
      phone: "+61 498 135 744",
    },
  },

  goals: [
    "Replace the need to build and maintain hundreds of separate website codebases.",
    "Connect approximately 350 existing domains to one centrally hosted application.",
    "Allow each domain to display its own website content, branding, pages, SEO and analytics.",
    "Provide a secure dashboard for creating, editing, previewing and publishing websites.",
    "Allow additional domains and websites to be added without rebuilding the platform.",
    "Provide controlled draft and publishing workflows so changes do not immediately affect live websites.",
    "Connect individual websites to Google Analytics 4 and display key performance data within the dashboard.",
    "Deploy platform improvements once and make those improvements available across all connected websites.",
    "Create an API-ready architecture that can support external AI and automation tools in the future.",
  ],

  sections: [
    {
      id: "overview",
      title: "Project overview",
      paragraphs: [
        "The proposed solution is a bespoke multi-domain website management platform designed to support a large portfolio of independently managed domain names.",
        "Rather than developing and hosting hundreds of unrelated websites, one Next.js application will serve all connected domains. The platform will identify the domain used by each visitor and retrieve the correct website content, configuration and search information from the central database.",
        "Each website will remain logically separate within the platform. A change made to one website's content, SEO, branding or contact information will apply only to that website unless an authorised user intentionally performs a broader platform-level update.",
        "This approach significantly reduces duplication, technical maintenance and future development effort while providing a scalable foundation for adding further domains.",
      ],
    },

    
    {
  id: "realWorldOutcome",
  title: "What you will receive in real terms",
  bullets: [
    "One centrally hosted Next.js application capable of serving approximately 350 existing domains and additional domains added later.",
    "One Supabase PostgreSQL database containing the individual website, page, content, SEO, domain, publishing and analytics records.",
    "A secure browser-based management dashboard for administering the complete website portfolio.",
    "A website creation workflow that allows an administrator to add a domain, select a template, enter business details, create pages and prepare the website for publication.",
    "The completed platform will be fully capable of rendering and publishing live websites as part of this project. Any website that has complete content, branding and configuration can be published immediately through the platform.",
    "The platform includes the capability to create, manage and publish unlimited websites. The preparation and population of individual website content is dependent on the availability of business information, images and other required assets.",
    "Independent content and SEO management for each website and each page.",
    "Draft, preview and publish controls so edits can be reviewed before appearing on a live domain.",
    "Domain connection instructions and automated connection-status checks.",
    "Individual GA4 configuration for each website, including Measurement ID and Property ID storage.",
    "A central analytics view showing website traffic, engagement and lead data across the portfolio.",
    "Automated deployment of platform software updates from the source-code repository to the hosted application.",
    "Page-level refresh and cache invalidation when website content is published.",
    "Version history and rollback capability for published website content.",
    "A modular API layer that can later be connected to external AI, reporting, CRM or automation services.",
  ],
},
    {
      id: "architecture",
      title: "Platform architecture",
      paragraphs: [
        "The solution will use a multi-tenant architecture. In this context, each website is treated as an individual tenant within one shared application.",
        "The application code controls how websites work and how reusable sections are rendered. Supabase stores what each website says, which domain it uses, which template it uses, how it is branded and what SEO information should be rendered.",
      ],
      bullets: [
        "Next.js application for public websites and the administration dashboard.",
        "React components for reusable website sections and dashboard interfaces.",
        "Supabase PostgreSQL database for websites, pages, domains, SEO, publishing records and analytics configuration.",
        "Supabase Authentication for secure user access.",
        "Supabase Storage or compatible object storage for logos, page images and media.",
        "Server-side domain detection based on the incoming hostname.",
        "Server-side data retrieval and page generation.",
        "Caching and selective page regeneration for fast public website delivery.",
        "Secure server APIs for publishing, domain management, analytics and future integrations.",
      ],
    },

    {
      id: "domainManagement",
      title: "Domain connection and management",
      paragraphs: [
        "Each domain will be pointed to the shared hosting environment using DNS records. The application will then recognise the incoming hostname and load the website record associated with that domain.",
        "The dashboard will maintain the relationship between each domain and its website content. Domains will not require separate copies of the application.",
      ],
      bullets: [
        "Add and manage domain records through the dashboard.",
        "Assign a domain to a specific website record.",
        "Display the required A, CNAME or other DNS records for connection.",
        "Track domain status as pending, awaiting DNS, connected or unavailable.",
        "Support both root and www domain variants.",
        "Verify that the domain resolves to the correct hosting environment.",
        "Display SSL and publication status where supported by the hosting infrastructure.",
        "Prevent duplicate domain assignment.",
        "Allow a connected domain to be replaced or redirected where required.",
      ],
    },

    {
      id: "newWebsiteWorkflow",
      title: "Adding new websites and domains",
      paragraphs: [
        "Once the platform is operating, an authorised user will be able to create additional websites through the dashboard without creating a new application or codebase.",
      ],
      bullets: [
        "Select Add Website.",
        "Enter the domain and website name.",
        "Select an available website template.",
        "Enter the business name, contact details, location and service information.",
        "Configure colours, logo and brand settings.",
        "Create or edit the required pages.",
        "Enter page-specific SEO information.",
        "Connect or enter the GA4 configuration.",
        "Review the website in preview mode.",
        "Update the domain DNS records using the instructions supplied by the platform.",
        "Publish the website once the domain connection has been confirmed.",
      ],
    },

    {
      id: "templates",
      title: "Website templates and reusable components",
      paragraphs: [
        "The platform will use reusable React components and controlled templates rather than generating unrestricted website layouts.",
        "This provides consistency, speed and maintainability while still allowing individual websites to use different content, colours, images, page combinations and settings.",
      ],
      bullets: [
        "Reusable header, footer and navigation components.",
        "Hero sections.",
        "Service and category sections.",
        "About and business-information sections.",
        "Call-to-action sections.",
        "Testimonials and trust sections.",
        "FAQ sections.",
        "Contact and lead-generation forms.",
        "Location and service-area sections.",
        "Configurable page ordering and visibility.",
        "Initial template system suitable for local service and trade websites.",
      ],
    },

    {
      id: "contentManagement",
      title: "Content management",
      paragraphs: [
        "Website content will be stored in Supabase and managed through the dashboard. It will not be maintained in individual JavaScript content files.",
        "Content will be structured so that each section can be edited independently and safely rendered by the correct React component.",
      ],
      bullets: [
        "Website-level business details and contact information.",
        "Page titles, slugs and page status.",
        "Hero headings, introductions and calls to action.",
        "Service descriptions.",
        "Content sections and supporting headings.",
        "FAQ questions and answers.",
        "Images and media references.",
        "Navigation labels and menu order.",
        "Draft and published content versions.",
        "Last-edited and last-published information.",
        "User attribution for content changes.",
      ],
    },

    {
      id: "seo",
      title: "SEO management",
      paragraphs: [
        "Supabase will store the SEO configuration, while Next.js will render that information into the finished HTML delivered to visitors and search engines.",
        "The customer will manage SEO through the dashboard rather than editing database records directly. Each SEO record will be linked to a specific website and page.",
      ],
      bullets: [
        "Page-specific SEO title.",
        "Meta description.",
        "Canonical URL.",
        "Index and follow settings.",
        "Open Graph title, description and image.",
        "Social-sharing metadata.",
        "Page heading and content hierarchy.",
        "Structured data and schema fields where supported.",
        "Automatic sitemap generation for each connected domain.",
        "Robots configuration.",
        "Page last-modified information.",
        "Website-level organisation and local-business information.",
        "Page preview showing how search metadata may appear.",
        "Draft, preview and publish workflow for SEO changes.",
      ],
    },

    {
      id: "publishing",
      title: "Draft, preview and publishing workflow",
      paragraphs: [
        "Editing a page will not automatically change the live website. Draft and published data will be separated so that content and SEO can be reviewed before publication.",
      ],
      bullets: [
        "Save content and SEO changes as a draft.",
        "Preview the website using draft content.",
        "Compare draft and currently published information.",
        "Publish changes to the selected website and page only.",
        "Refresh the affected page cache after publication.",
        "Record the publishing date and authorised user.",
        "Retain previous versions for rollback.",
        "Unpublish or disable a page without deleting its content.",
        "Support controlled whole-site publication when preparing a new domain.",
      ],
    },

    {
      id: "analytics",
      title: "Google Analytics 4 integration",
      paragraphs: [
        "Each website can be connected to a GA4 property and web data stream. The correct Measurement ID will be rendered only on the website to which it has been assigned.",
        "The platform will also connect to the Google Analytics Data API so selected reporting information can be displayed inside the central dashboard.",
      ],
      bullets: [
        "Store a separate GA4 Measurement ID and Property ID for each website.",
        "Load the correct Google tag based on the visitor's domain.",
        "Track page views and agreed lead-generation events.",
        "Track contact-form submissions.",
        "Track telephone-link clicks.",
        "Track email-link clicks.",
        "Track quote and enquiry actions where supported.",
        "Display GA4 connection status in the dashboard.",
        "Display key metrics such as users, sessions, views, engagement and lead events.",
        "Provide a portfolio summary covering connected websites.",
        "Provide website-level analytics detail.",
        "Cache analytics summaries to avoid unnecessary Google API requests.",
        "Allow analytics information to be refreshed on a scheduled or controlled basis.",
      ],
    },

    {
      id: "dashboard",
      title: "Management dashboard",
      bullets: [
        "Secure user login.",
        "Portfolio overview showing all websites and domains.",
        "Search and filter websites by domain, status or name.",
        "Website connection status.",
        "Draft and publication status.",
        "GA4 connection status.",
        "Website creation and setup workflow.",
        "Page and content editor.",
        "SEO editor.",
        "Domain settings.",
        "Analytics overview.",
        "Media management.",
        "Version history.",
        "Preview and publish controls.",
        "Basic user roles and permissions.",
        "Audit information showing who changed and published content.",
      ],
    },

    {
      id: "deployment",
      title: "Automated deployment and updates",
      paragraphs: [
        "Content changes and application changes will be handled differently.",
        "Normal content and SEO updates will be published from the database and will not require a full software deployment. Changes to the platform code, templates or dashboard will follow an automated deployment workflow.",
      ],
      bullets: [
        "Source-controlled application code.",
        "Automated build and deployment following approved code changes.",
        "One software deployment applied across all connected domains.",
        "Controlled environment variables and credentials.",
        "Build validation and deployment logging.",
        "Page-level cache refresh following content publication.",
        "Separation of development and production environments where practical.",
        "Rollback process for failed software deployments.",
      ],
    },

    {
      id: "performance",
      title: "Performance and caching",
      paragraphs: [
        "Although website content is stored in a database, visitors should not be required to wait for repeated database queries on every page view.",
        "The platform will use server-side rendering, caching and selective page regeneration so published websites are delivered with performance comparable to modern static websites.",
      ],
      bullets: [
        "Cached delivery of published pages.",
        "Selective regeneration of affected pages after publication.",
        "Cached domain-to-website lookups.",
        "Optimised database queries.",
        "Server-side content retrieval.",
        "Image optimisation and responsive image delivery.",
        "Analytics-data caching.",
        "Web application and database regions selected to minimise unnecessary network delay.",
      ],
    },

    {
      id: "api",
      title: "API-ready and future AI integration",
      paragraphs: [
        "A built-in AI content assistant is not included in this proposal. The platform itself will provide the content management, SEO, publishing and domain-management capabilities.",
        "The system will be structured so that authorised external services can later create or update draft content through controlled APIs. This avoids locking the platform into one AI provider.",
      ],
      bullets: [
        "Secure API architecture for future integrations.",
        "Ability to connect services such as OpenAI, Claude, Gemini or other content tools later.",
        "External tools would update draft records rather than publish directly.",
        "Human review and publication would remain controlled through the dashboard.",
        "Future AI integration can be scoped and priced independently.",
      ],
    },

    {
      id: "leadManagement",
      title: "Forms and lead routing",
      bullets: [
        "Reusable contact and enquiry forms.",
        "Website and domain attribution for every enquiry.",
        "Server-side form processing.",
        "Spam and abuse protection.",
        "Configurable recipient email per website.",
        "Lead-event tracking through GA4.",
        "Database logging of submissions where included in the final implementation.",
        "API-ready structure for future CRM integration.",
      ],
    },

    {
      id: "security",
      title: "Security and access control",
      bullets: [
        "Supabase authentication for dashboard users.",
        "Role-based access controls.",
        "Server-side validation for protected operations.",
        "No privileged database credentials exposed in the browser.",
        "Row-level security policies where appropriate.",
        "Protected publishing actions.",
        "Audit information for critical content changes.",
        "Secure storage of Google and third-party integration credentials.",
        "Input validation and sanitisation.",
        "Rate limiting for sensitive public and administration endpoints where required.",
      ],
    },

    {
      id: "initialImplementation",
      title: "Initial implementation and domain onboarding",
      paragraphs: [
        "The platform build includes the technical foundation, dashboard, initial templates, publishing system and domain-management capability.",
        "The initial connection of the domain portfolio will require access to the relevant domain registrar or DNS provider. The level of automation available will depend on how the domains are currently registered and managed.",
      ],
      bullets: [
        "Review of the current domain portfolio and DNS arrangements.",
        "Definition of the standard domain-connection process.",
        "Configuration of the shared hosting environment.",
        "Initial platform domain and SSL configuration.",
        "Import or setup method for the existing domain list.",
        "Connection-status reporting.",
        "Technical guidance for bulk DNS changes where registrar capability allows.",
        "Testing across a representative selection of connected domains.",
      ],
    },

    {
      id: "ownerInputs",
      title: "What Will's Plumbing provides",
      bullets: [
        "Complete list of domains to be managed.",
        "Access to the relevant registrar, DNS provider or Cloudflare account.",
        "Confirmation of the intended business purpose of each website.",
        "Business names, service areas and contact details.",
        "Existing content and SEO records where available.",
        "Logos, images and brand assets.",
        "Google account access required to create or connect GA4 properties.",
        "Approval of the initial templates and content structure.",
        "Timely review and approval during development and testing.",
      ],
    },

    {
      id: "limitations",
      title: "Scope boundaries",
      bullets: [
        "Domain registration and renewal fees are not included.",
        "Third-party hosting, Supabase, Google, email or infrastructure charges are not included unless specifically stated.",
        "Creation of unique written content for every domain is not included.",
        "Manual population of hundreds of complete websites is not included unless separately itemised.",
        "Built-in AI content generation is not included.",
        "Google Ads management is not included.",
        "Search Console reporting is not included unless added to the final scope.",
        "CRM integrations are not included unless specifically listed.",
        "Automated DNS management depends on the registrar or DNS provider offering a suitable API.",
        "Google Analytics reporting is subject to Google's API permissions, quotas and data-processing timeframes.",
      ],
    },

    {
      id: "delivery",
      title: "Indicative delivery approach",
      bullets: [
        "Discovery, requirements confirmation and domain audit.",
        "Technical architecture and database design.",
        "Supabase database and authentication setup.",
        "Multi-domain Next.js application foundation.",
        "Initial website templates and reusable components.",
        "Dashboard and website-management workflow.",
        "Content, SEO and publishing systems.",
        "Domain onboarding and connection-status workflow.",
        "GA4 integration and reporting dashboard.",
        "Automated deployment and caching configuration.",
        "Security review, testing and quality assurance.",
        "Production deployment, documentation and handover.",
      ],
    },

    {
      id: "support",
      title: "Support and ongoing development",
      paragraphs: [
        "The quoted build establishes the core platform. Ongoing hosting, monitoring, maintenance, support and future feature development should be treated as an ongoing managed software service rather than standard brochure-site hosting.",
      ],
      bullets: [
        "30-day technical defect warranty following production launch.",
        "Ongoing hosting and platform maintenance to be confirmed once the final infrastructure is selected.",
        "Future templates, integrations, dashboard features and automation quoted separately.",
        "Content entry and content production can be provided separately if required.",
        "Platform changes outside the agreed scope are billed at A$150 per hour unless covered by a separate support agreement.",
      ],
    },
  ],

  pricing: {
    currency: "AUD",
    disclaimer:
      "Pricing is based on the platform scope described in this proposal. Significant changes to domain-management requirements, analytics configuration, website templates, content migration or third-party integrations may require a revised quotation.",
    items: [
      {
        label: "Multi-domain website management platform",
        description:
          "Design and development of the Next.js and Supabase platform, including multi-domain routing, database architecture, secure dashboard, website and page management, SEO controls, draft and publishing workflow, reusable templates, domain onboarding, GA4 configuration and reporting, caching, deployment automation, testing and production launch.",
        amount: 29500,
      },
    ],
    showTotals: true,
    paymentSchedule: [
      {
        label: "Project commencement",
        detail:
          "Due on acceptance of the proposal and before discovery and platform architecture commence.",
        percentage: 50,
      },
      {
        label: "Platform foundation milestone",
        detail:
          "Due when the multi-domain application, database structure and core dashboard are operational in the development environment.",
        percentage: 30,
      },
      {
        label: "Production launch",
        detail:
          "Due following final testing and before deployment of the completed platform to production.",
        percentage: 20,
      },
    ],
  },

  maintenancePlans: [
  {
    name: "Managed platform hosting and maintenance",
    priceLabel: "A$750 per month",
    tagline:
      "Fully managed hosting, monitoring, maintenance and technical oversight provided by 2merative for the shared multi-domain platform.",
    includes: [
      "Production hosting of the Next.js application.",
      "Supabase database, authentication and storage oversight.",
      "Management of the primary hosting and deployment environment.",
      "Deployment monitoring and investigation of failed deployments.",
      "Security, framework and dependency updates.",
      "Routine platform health and availability monitoring.",
      "Database backup and recovery oversight.",
      "SSL and shared platform domain configuration oversight.",
      "Up to two hours of technical support or minor platform maintenance each month.",
      "Investigation and resolution of hosting-related technical issues.",
      "Monthly review of infrastructure usage and performance.",
      "Reasonable third-party infrastructure costs included within normal usage limits.",
      "Excess usage, premium services, major incidents and development work are quoted separately.",
    ],
  },
],

  faqs: [
    {
      question: "Is this 350 separate websites?",
      answer:
        "No. It is one centrally hosted application serving approximately 350 domain names. Each domain has its own website record, pages, content, SEO settings, branding and analytics configuration, but all websites share the same underlying application and reusable components.",
    },
    {
      question: "Can more domains be added later?",
      answer:
        "Yes. An authorised user will be able to create another website through the dashboard, assign its domain, select a template, enter its content and SEO information, connect analytics, complete the required DNS changes and publish it.",
    },
    {
      question: "Where is the website content stored?",
      answer:
        "Content is stored in the Supabase PostgreSQL database. It is not stored in hundreds of JavaScript files. The Next.js application reads the published content and renders it using shared React components.",
    },
    {
      question: "Can SEO be edited independently for every website?",
      answer:
        "Yes. The dashboard will allow authorised users to select a website and page, edit its SEO title, meta description, canonical configuration, indexing settings, social metadata and supported structured data, then preview and publish those changes specifically to that website.",
    },
    {
      question: "Does changing content require a new deployment?",
      answer:
        "No. Normal content and SEO changes are saved to Supabase and published through the dashboard. Only changes to the platform software, templates or shared functionality require a new application deployment.",
    },
    {
      question: "How are the domains connected?",
      answer:
        "Each domain is pointed to the shared hosting environment through DNS. The platform identifies the incoming hostname and loads the website record assigned to that domain. The dashboard will provide connection instructions and display connection status.",
    },
    {
      question: "Can DNS changes be completely automated?",
      answer:
        "Potentially, but this depends on where the domains are registered and whether the registrar or DNS provider offers a suitable API. The initial platform will support domain creation, connection instructions and verification. Deeper DNS automation can be assessed once the existing domain portfolio has been audited.",
    },
    {
      question: "How does Google Analytics work?",
      answer:
        "Each website can store its own GA4 Measurement ID and Property ID. The application loads the correct Google tag for the relevant domain, while the dashboard retrieves selected reporting data through Google's Analytics Data API.",
    },
    {
      question: "Does Supabase manage the SEO?",
      answer:
        "Supabase stores the SEO information. The dashboard allows users to edit it, and Next.js renders it into the final page HTML that is read by search engines.",
    },
    {
      question: "Is AI included?",
      answer:
        "No built-in AI assistant is included in this proposal. The platform will be API-ready so external AI tools can later create or update draft content without tying the system to one AI provider. Any AI integration would be separately scoped and priced.",
    },
    {
      question: "Will a database make the websites slower?",
      answer:
        "The platform will use cached published pages, efficient queries and selective regeneration. Visitors will generally receive cached website output rather than waiting for the database to be queried repeatedly on every page view.",
    },
    {
      question: "Why is this more than a standard website build?",
      answer:
        "This is a software platform rather than an individual website. It includes a database-backed CMS, multi-domain routing, user authentication, website creation, SEO management, publishing controls, domain verification, analytics integration, deployment automation and ongoing scalability.",
    },
  ],

 acceptance: {
  heading: "Next steps",
  blurb:
    "Acceptance of this proposal will allow the discovery and technical architecture phase to commence. This initial stage will confirm the domain portfolio, infrastructure, platform architecture, data model, template strategy and Google Analytics implementation before development begins.",
  terms: [
    "You confirm that you are authorised to approve this work on behalf of Will's Plumbing.",
    "The project scope is limited to the inclusions described in this proposal.",
    "The initial 50% payment is due upon acceptance of this proposal.",
    "The estimated delivery timeframe is approximately 20–24 weeks from project commencement, subject to timely access, client approvals and the availability of required information.",
    "Access to domain registrars, DNS providers and Google accounts must be supplied when required.",
    "External hosting, Supabase, domain registration, Google services and other third-party service fees are additional unless otherwise stated.",
    "Creation and population of unique content for each website is not included unless separately agreed.",
    "Built-in AI content generation is not included in this proposal.",
    "Work outside the agreed scope will be quoted separately or billed at A$150 per hour with prior approval.",
    "Production deployment will occur following successful testing, client approval and payment of the final milestone.",
    "The development schedule assumes the project proceeds continuously. Delays in approvals, content delivery or required access may extend the estimated completion date.",
  ],
},
};

export const quoteData = jmdsProposal;

export default jmdsProposal;